/**
 * /settings/invoices — Historique des factures
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { PublicHeader, Breadcrumb } from '@/components/jurisprudence';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { SettingsNav } from './SettingsProfile';
import { getDateLocale } from '@/i18n';

const CHECKOUT_BASE = '/api/checkout';

async function apiFetch(path: string, token: string, options: RequestInit = {}) {
  const res = await fetch(`${CHECKOUT_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

interface Invoice {
  id:          string;
  number:      string;
  date:        string;        // ISO
  amount:      number;        // en centimes
  currency:    string;
  status:      string;        // "paid" | "open" | "void"
  pdf_url:     string | null;
  hosted_url:  string | null;
  description: string;
}

export default function SettingsInvoices() {
  const { t } = useTranslation();
  const navigate                    = useNavigate();
  const { user, loading, getToken } = usePublicAuth();

  const [invoices,  setInvoices]  = useState<Invoice[]>([]);
  const [fetching,  setFetching]  = useState(true);
  const [error,     setError]     = useState('');
  const [loadingPortal, setLoadingPortal] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate('/login?redirect=/settings/invoices', { replace: true });
  }, [user, loading, navigate]);

  const loadInvoices = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setFetching(true);
    try {
      const data = await apiFetch('/invoices', token);
      setInvoices(data.invoices || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setFetching(false);
    }
  }, [getToken]);

  useEffect(() => { if (user) loadInvoices(); }, [user, loadInvoices]);

  const handlePortal = async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingPortal(true);
    try {
      const data = await apiFetch('/portal', token, { method: 'POST' });
      window.location.href = data.url;
    } catch (e: any) {
      setError(e.message);
      setLoadingPortal(false);
    }
  };

  const formatAmount = (amount: number, currency: string) =>
    new Intl.NumberFormat(getDateLocale(), {
      style: 'currency', currency: currency.toUpperCase(),
    }).format(amount / 100);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(getDateLocale(), {
      day: 'numeric', month: 'long', year: 'numeric',
    });

  const statusLabel: Record<string, { label: string; cls: string }> = {
    paid:  { label: t('settings.invoices.status.paid'),    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    open:  { label: t('settings.invoices.status.open'), cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    void:  { label: t('settings.invoices.status.void'),  cls: 'bg-muted text-muted-foreground' },
    draft: { label: t('settings.invoices.status.draft'),cls: 'bg-muted text-muted-foreground' },
  };

  if (loading) return <Spinner />;
  if (!user)   return null;

  return (
    <>
      <Helmet><title>{t('settings.invoices.page_title')}</title></Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />
        <Breadcrumb items={[{ label: t('settings.breadcrumb_home'), to: '/' }, { label: t('settings.my_account') }]} />
        <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-10">
          <SettingsNav active="invoices" />

          {error && (
            <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
              {error}
            </div>
          )}

          <section className="rounded-xl border bg-card p-6 mt-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold">{t('settings.invoices.title')}</h2>
              <button
                onClick={handlePortal}
                disabled={loadingPortal}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border rounded-lg px-3 py-1.5 hover:bg-accent transition-colors disabled:opacity-50"
              >
                {loadingPortal ? (
                  <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                )}
                {t('settings.invoices.stripe_portal')}
              </button>
            </div>

            {fetching ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                </div>
                <p className="text-sm font-medium text-foreground mb-1">{t('settings.invoices.empty_title')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('settings.invoices.empty_description')}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {invoices.map(inv => {
                  const st = statusLabel[inv.status] || { label: inv.status, cls: 'bg-muted text-muted-foreground' };
                  return (
                    <div key={inv.id} className="flex items-center justify-between py-4 gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-medium">{formatDate(inv.date)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>
                            {st.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {inv.number}{inv.description ? ` · ${inv.description}` : ''}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0">
                        <p className="text-sm font-semibold tabular-nums">
                          {formatAmount(inv.amount, inv.currency)}
                        </p>
                        <div className="flex items-center gap-1.5">
                          {inv.pdf_url && (
                            <a
                              href={inv.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t('settings.invoices.download_pdf')}
                              className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                              </svg>
                            </a>
                          )}
                          {inv.hosted_url && (
                            <a
                              href={inv.hosted_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t('settings.invoices.view_invoice')}
                              className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  );
}